/**
 * LandingPage — public marketing homepage for compliancehubau.com.
 * Standalone route (no app Shell). Dark navy/mint brand per design_guidelines.json.
 * Routes: "/" (guests), "/home" (always, incl. logged-in users).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import HeroDemo from "@/components/HeroDemo";
import { usePwaInstall } from "@/lib/usePwaInstall";
import { toast } from "sonner";
import {
  ClipboardList, Camera, PenTool, Video, Mic, ShieldCheck, Sparkles, FileText,
  CloudOff, Landmark, BadgeCheck, Users, Lock, ArrowRight, Menu, X, Check, Download,
  ChevronDown, Send, Loader2, Building2, Flame, Bug, HeartPulse, Waves, FileStack,
  ScrollText, MapPin, Layers, Workflow, CircleCheck, CircleX, Timer, Mail,
} from "lucide-react";

const NAVY = "#0B1020";
const MINT = "#00F2C3";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-5 sm:px-8 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const Label = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{ color: MINT }}>{children}</p>
);

const H2 = ({ children }) => (
  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{children}</h2>
);

/* ---------------------------------------------------------------- header */
const NAV = [
  ["Platform", "#platform"], ["Features", "#features"], ["Industries", "#industries"],
  ["AI", "#ai"], ["Security", "#security"], ["Pricing", "#pricing"], ["Help", "/help"],
];

/* Visible install control — native prompt when available, device-aware
   fallback to /install otherwise. Hidden once the app is installed. */
const InstallButton = ({ mobile = false, onDone }) => {
  const { installed, promptInstall } = usePwaInstall();
  const navigate = useNavigate();
  if (installed) return null;
  const handle = async () => {
    const outcome = await promptInstall();
    if (onDone) onDone();
    if (outcome === "unavailable" || outcome === "ios") navigate("/install");
  };
  if (mobile) {
    return (
      <button onClick={handle} data-testid="lp-install-app-mobile"
        className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold border border-white/20 text-white hover:bg-white/5 transition-all">
        <Download size={16} strokeWidth={2.5}/> Install App
      </button>
    );
  }
  return (
    <button onClick={handle} data-testid="lp-install-app"
      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md font-semibold text-sm text-white border border-white/20 hover:border-white/40 hover:bg-white/5 transition-all">
      <Download size={15} strokeWidth={2.5}/> Install App
    </button>
  );
};

const Header = ({ authed }) => {
  const [open, setOpen] = useState(false);
  const go = (href) => { setOpen(false); };
  return (
    <header className="sticky top-0 z-50 border-b border-white/5" style={{ background: "rgba(11,16,32,0.85)", backdropFilter: "blur(16px)" }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-6">
        <a href="#top" className="flex items-center gap-2.5 shrink-0" data-testid="lp-logo">
          <img src="/ch-logo.png" alt="Compliance Hub" className="h-9 w-auto object-contain"/>
          <span className="hidden sm:block leading-tight">
            <span className="block text-white font-bold text-[15px] tracking-wide" style={{ fontFamily: "Outfit, sans-serif" }}>COMPLIANCE <span style={{ color: MINT }}>HUB</span></span>
            <span className="block text-[9px] tracking-[0.18em] uppercase text-slate-500">All Inspections. One Hub.</span>
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-6 mx-auto" aria-label="Main navigation">
          {NAV.map(([label, href]) => href.startsWith("/")
            ? <Link key={label} to={href} className="text-sm text-slate-300 hover:text-white transition-colors">{label}</Link>
            : <a key={label} href={href} className="text-sm text-slate-300 hover:text-white transition-colors">{label}</a>)}
        </nav>
        <div className="hidden lg:flex items-center gap-3 shrink-0 ml-auto">
          <InstallButton />
          {authed ? (
            <Link to="/select-module" data-testid="lp-open-app"
              className="px-5 py-2.5 rounded-md font-semibold text-sm transition-all hover:opacity-90"
              style={{ background: MINT, color: NAVY }}>Open Compliance Hub</Link>
          ) : (
            <>
              <Link to="/login" data-testid="lp-login" className="text-sm font-semibold text-slate-200 hover:text-white transition-colors">Log In</Link>
              <Link to="/signup" data-testid="lp-get-started"
                className="px-5 py-2.5 rounded-md font-semibold text-sm transition-all hover:opacity-90"
                style={{ background: MINT, color: NAVY }}>Get Started</Link>
            </>
          )}
        </div>
        <button className="lg:hidden ml-auto text-white p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="lp-mobile-menu">
          {open ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 px-5 py-4 space-y-1" style={{ background: NAVY }} data-testid="lp-mobile-nav">
          {NAV.map(([label, href]) => href.startsWith("/")
            ? <Link key={label} to={href} onClick={() => go(href)} className="block py-2.5 text-slate-200 font-medium">{label}</Link>
            : <a key={label} href={href} onClick={() => go(href)} className="block py-2.5 text-slate-200 font-medium">{label}</a>)}
          <div className="pt-3">
            <InstallButton mobile onDone={() => setOpen(false)} />
          </div>
          <div className="pt-3 flex gap-3">
            {authed ? (
              <Link to="/select-module" className="flex-1 text-center px-4 py-3 rounded-md font-semibold" style={{ background: MINT, color: NAVY }}>Open Compliance Hub</Link>
            ) : (
              <>
                <Link to="/login" className="flex-1 text-center px-4 py-3 rounded-md font-semibold border border-white/20 text-white">Log In</Link>
                <Link to="/signup" className="flex-1 text-center px-4 py-3 rounded-md font-semibold" style={{ background: MINT, color: NAVY }}>Get Started</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------------------------------------------------------------- CSS product mockup */
const AppMockup = () => (
  <div className="relative mx-auto max-w-4xl" aria-hidden="true">
    <div className="absolute -inset-8 rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(closest-side, rgba(0,242,195,0.18), transparent)" }}/>
    <div className="relative rounded-xl border border-white/10 overflow-hidden shadow-2xl" style={{ background: "#13182A" }}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10" style={{ background: "#0E1526" }}>
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/70"/><span className="w-2.5 h-2.5 rounded-full bg-amber-400/70"/><span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70"/>
        <span className="ml-3 text-[11px] text-slate-500 font-medium">compliancehubau.com — Inspection #2417</span>
      </div>
      <div className="grid grid-cols-12">
        <div className="hidden sm:block col-span-3 border-r border-white/10 p-4 space-y-2.5" style={{ background: "#0E1526" }}>
          {["Dashboard", "Inspections", "Statutory", "Documents", "Team"].map((t, i) => (
            <div key={t} className={`px-3 py-2 rounded text-[12px] font-medium ${i === 1 ? "text-[#0B1020]" : "text-slate-400"}`}
              style={i === 1 ? { background: MINT } : {}}>{t}</div>
          ))}
        </div>
        <div className="col-span-12 sm:col-span-9 p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-white font-semibold text-sm">12 Example Street, Penrith NSW</p>
              <p className="text-slate-500 text-[11px]">Pool & Spa — Barrier Inspection</p>
            </div>
            <span className="px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide bg-red-500/15 text-red-300 border border-red-400/30">Non-compliant</span>
          </div>
          {[["Gate self-closes from all positions", false], ["Barrier height ≥ 1200mm", true], ["No climbable objects in NCZ", true]].map(([q, ok]) => (
            <div key={q} className="flex items-center gap-3 rounded-lg border border-white/10 px-3.5 py-3" style={{ background: "#1A2036" }}>
              {ok ? <CircleCheck size={16} className="text-emerald-400 shrink-0"/> : <CircleX size={16} className="text-red-400 shrink-0"/>}
              <span className="text-[12px] text-slate-300 flex-1">{q}</span>
              <span className="hidden sm:flex gap-1">{[1, 2].map((n) => <span key={n} className="w-7 h-7 rounded border border-white/10 bg-white/5 flex items-center justify-center"><Camera size={11} className="text-slate-500"/></span>)}</span>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <span className="px-3.5 py-2 rounded-md text-[11px] font-semibold" style={{ background: MINT, color: NAVY }}>Generate Notice</span>
            <span className="px-3.5 py-2 rounded-md text-[11px] font-semibold border border-white/15 text-slate-300">Photo Report</span>
          </div>
        </div>
      </div>
    </div>
    <div className="absolute -bottom-6 -right-3 sm:-right-8 w-32 sm:w-40 rounded-[1.4rem] border-4 border-slate-800 overflow-hidden shadow-2xl" style={{ background: "#0E1526" }}>
      <div className="p-3 space-y-2">
        <div className="h-1 w-8 mx-auto rounded-full bg-white/20"/>
        <p className="text-[9px] font-semibold text-white">Sync status</p>
        <div className="flex items-center gap-1.5 rounded bg-white/5 border border-white/10 px-2 py-1.5">
          <CloudOff size={10} className="text-amber-400"/><span className="text-[8px] text-slate-400">Offline — 3 queued</span>
        </div>
        <div className="flex items-center gap-1.5 rounded bg-white/5 border border-white/10 px-2 py-1.5">
          <Camera size={10} style={{ color: MINT }}/><span className="text-[8px] text-slate-400">14 photos saved</span>
        </div>
        <div className="h-14 rounded border border-white/10 bg-gradient-to-br from-[#1E1B4B] to-[#13182A]"/>
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------------- data */
const MODULES = [
  { icon: Waves, name: "Pool & Spa", desc: "Digital inspections, evidence capture, compliance documentation and statutory workflows." },
  { icon: HeartPulse, name: "Health", desc: "Customisable inspection workflows designed for environmental and public health inspections." },
  { icon: Building2, name: "Building Compliance", desc: "Inspection, investigation, evidence and enforcement workflows." },
  { icon: Bug, name: "Pest & Building", desc: "Structured inspection checklists, photographic evidence and professional reporting." },
  { icon: BadgeCheck, name: "Certification", desc: "Inspection and certification workflows with professional documentation." },
  { icon: Flame, name: "Fire Safety", desc: "Fire-safety inspection workflows, evidence, compliance tracking and reporting." },
];

const FIELD_FEATURES = [
  { icon: ClipboardList, t: "Smart Digital Checklists", d: "Complete structured inspections quickly with module-specific checklist workflows." },
  { icon: Camera, t: "Photos & Evidence", d: "Capture multiple photos directly from the inspection — kept with the finding." },
  { icon: PenTool, t: "Photo Annotation", d: "Add arrows, circles, rectangles and notes to clearly identify compliance issues." },
  { icon: Video, t: "Video Evidence", d: "Attach video evidence where required." },
  { icon: Mic, t: "Voice-Driven Inspection", d: "Reduce typing while working onsite." },
  { icon: ShieldCheck, t: "Automatic Compliance Status", d: "Clearly identify Compliant, Non-Compliant and Incomplete outcomes." },
  { icon: ScrollText, t: "Inspection Audit Trail", d: "Maintain a record of important inspection actions and changes." },
];

const WORKFLOW = ["Create Inspection", "Inspect", "Capture Evidence", "Assess Compliance", "Generate Documentation", "Send", "Track", "Finalise"];

const FAQS = [
  ["What is Compliance Hub?", "Compliance Hub is an inspection and compliance management platform that brings digital inspections, evidence capture, professional documentation and regulatory workflows together in one secure system."],
  ["Who can use Compliance Hub?", "Councils, private certifiers, inspection businesses, building compliance teams, fire safety professionals, health officers and multi-disciplinary compliance organisations — solo operators through to large teams."],
  ["Does Compliance Hub work offline?", "Yes. Compliance Hub is built for field conditions — inspections continue without reception, save locally on your device and synchronise automatically when connection returns, with upload queues and sync status indicators."],
  ["Can I use Compliance Hub on my phone?", "Yes. Compliance Hub is a progressive web app that installs to your phone or tablet and is designed mobile-first for field inspection work."],
  ["Does Compliance Hub support teams?", "Yes. Organisation owners can invite team members, who inherit the organisation's modules, templates, subscription and settings, with role-based permissions."],
  ["Can I create my own checklists?", "Yes. The Checklist Builder lets you create sections, custom questions, photo requirements and mandatory fields — or describe the inspection and let AI assist in drafting the checklist."],
  ["Does Compliance Hub use AI?", "Yes — AI can assist with reviewing inspection photos, drafting compliance wording, building checklists and in-app support. AI features adapt to the relevant inspection discipline."],
  ["Does AI make the final compliance decision?", "No. AI is designed to assist the inspector. Final compliance decisions remain with the authorised professional."],
  ["Can councils use Compliance Hub?", "Yes. Councils get a regulatory tracker for notices, orders and directions, automated follow-up reminders, council-specific workflows, organisation-controlled templates and team management."],
  ["Can private certifiers use Compliance Hub?", "Yes. Certifiers get inspection management, statutory deadline tracking, council notification workflows, business branding, reinspection workflows and customer communication tools."],
  ["Can Compliance Hub integrate with other systems?", "Integration capability is being progressively developed. Stripe billing is live today; connections for records management, council and calendar systems are on the roadmap."],
  ["Where is my inspection information stored?", "Inspection data is stored in a secure cloud database, with photos and videos held in private cloud object storage. Each organisation's data is strictly separated, protected by authenticated access, role-based permissions and audit trails."],
];

const AUDIENCES = ["Local councils", "Private certifiers", "Inspection businesses", "Building compliance teams",
  "Fire safety professionals", "Health & environmental health officers", "Building inspectors", "Multi-disciplinary compliance organisations"];

/* ---------------------------------------------------------------- pricing */
const TIER_FEATURES = {
  solo: ["1 user", "Core inspection workflow", "Professional PDF reports", "Offline field mode", "Customer portal links"],
  pro: ["Team seats included", "All inspection modules", "AI-assisted workflows", "Custom checklists & templates", "Statutory & regulatory tracking", "Priority support"],
  enterprise: ["Larger teams & councils", "Organisation-wide configuration", "Advanced administration", "Onboarding assistance", "Custom requirements — talk to us"],
};

const PricingSection = () => {
  const [packages, setPackages] = useState(null);
  const [cadence, setCadence] = useState("monthly");
  useEffect(() => {
    api.get("/billing/packages").then((r) => setPackages(r.data.packages || [])).catch(() => setPackages([]));
  }, []);
  const byTier = useMemo(() => {
    const m = {};
    for (const p of packages || []) { m[p.tier] = m[p.tier] || {}; m[p.tier][p.cadence] = p; }
    return m;
  }, [packages]);
  const tiers = ["solo", "pro", "enterprise"].filter((t) => byTier[t]);
  return (
    <Section id="pricing" className="py-24">
      <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
        <Label>Pricing</Label>
        <H2>Simple plans that grow with your team</H2>
        <p className="mt-4 text-slate-400">One subscription per organisation — team members share the plan. Prices in AUD.</p>
        <div className="mt-6 inline-flex rounded-md border border-white/15 p-1" role="tablist">
          {["monthly", "annual"].map((c) => (
            <button key={c} onClick={() => setCadence(c)} data-testid={`lp-cadence-${c}`} role="tab" aria-selected={cadence === c}
              className={`px-5 py-2 rounded text-sm font-semibold capitalize transition-colors ${cadence === c ? "text-[#0B1020]" : "text-slate-300"}`}
              style={cadence === c ? { background: MINT } : {}}>{c}</button>
          ))}
        </div>
      </motion.div>
      {packages === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-500"/></div>
      ) : tiers.length === 0 ? (
        <p className="text-center text-slate-500 py-12">Plans are loading — see <Link to="/pricing" className="underline" style={{ color: MINT }}>full pricing</Link>.</p>
      ) : (
        <div className="mt-12 grid md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((tier) => {
            const pkg = byTier[tier][cadence] || byTier[tier].monthly;
            const highlight = tier === "pro";
            return (
              <motion.div {...fadeUp} key={tier} data-testid={`lp-tier-${tier}`}
                className="relative rounded-xl border p-8 flex flex-col"
                style={{ background: "#13182A", borderColor: highlight ? MINT : "rgba(255,255,255,0.1)", boxShadow: highlight ? "0 0 40px rgba(0,242,195,0.12)" : "none" }}>
                {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: MINT, color: NAVY }}>Most popular</span>}
                <p className="text-sm font-bold uppercase tracking-wider text-slate-400 capitalize">{pkg?.name || tier}</p>
                {pkg ? (
                  <p className="mt-3"><span className="text-4xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }} data-testid={`lp-price-${tier}`}>A${Math.round(pkg.amount).toLocaleString()}</span>
                    <span className="text-slate-500 text-sm"> / {pkg.cadence === "annual" ? "year" : "month"}</span></p>
                ) : <p className="mt-3 text-2xl font-bold text-white">Contact us</p>}
                <ul className="mt-6 space-y-2.5 flex-1">
                  {(TIER_FEATURES[tier] || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check size={15} className="mt-0.5 shrink-0" style={{ color: MINT }}/>{f}</li>
                  ))}
                </ul>
                <Link to={tier === "enterprise" ? "#contact" : "/signup"}
                  onClick={tier === "enterprise" ? (e) => { e.preventDefault(); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); } : undefined}
                  data-testid={`lp-tier-cta-${tier}`}
                  className={`mt-8 text-center px-5 py-3 rounded-md font-semibold text-sm transition-all ${highlight ? "hover:opacity-90" : "border border-white/20 text-white hover:bg-white/5"}`}
                  style={highlight ? { background: MINT, color: NAVY } : {}}>
                  {tier === "enterprise" ? "Contact Us" : "Get Started"}
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
      <p className="mt-6 text-center text-xs text-slate-600">Full plan details on the <Link to="/pricing" className="underline">pricing page</Link>.</p>
    </Section>
  );
};

/* ---------------------------------------------------------------- contact form */
const ContactSection = () => {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", organisation: "",
    organisation_type: "council", team_size: "", modules: [], message: "", website_url: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.organisation) { toast.error("Please complete name, email and organisation"); return; }
    setBusy(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — please try again");
    } finally { setBusy(false); }
  };
  const inputCls = "w-full h-12 rounded-md border border-white/15 bg-white/5 px-3.5 text-[15px] text-white placeholder:text-slate-600 focus:border-[#00F2C3] focus:outline-none transition-colors";
  return (
    <Section id="contact" className="py-24">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <motion.div {...fadeUp}>
          <Label>Contact</Label>
          <H2>Talk to Compliance Hub</H2>
          <p className="mt-4 text-slate-400 leading-relaxed">Interested for your council, certification business or inspection team? Tell us about your organisation and we'll be in touch.</p>
          <div className="mt-8 space-y-3 text-sm text-slate-400">
            <p className="flex items-center gap-2.5"><Timer size={16} style={{ color: MINT }}/>Fast response from the Compliance Hub team</p>
            <p className="flex items-center gap-2.5"><Users size={16} style={{ color: MINT }}/>Organisation onboarding &amp; team setup help</p>
            <p className="flex items-center gap-2.5"><Landmark size={16} style={{ color: MINT }}/>Council and enterprise arrangements available</p>
          </div>
        </motion.div>
        <motion.div {...fadeUp}>
          {sent ? (
            <div className="rounded-xl border border-white/10 p-10 text-center" style={{ background: "#13182A" }} data-testid="lp-contact-success">
              <CircleCheck size={40} className="mx-auto mb-4" style={{ color: MINT }}/>
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Enquiry sent</h3>
              <p className="mt-2 text-slate-400 text-sm">Thanks — we'll be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-xl border border-white/10 p-6 sm:p-8 space-y-4" style={{ background: "#13182A" }} data-testid="lp-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input aria-label="First name" placeholder="First name *" value={form.first_name} onChange={(e) => up("first_name", e.target.value)} className={inputCls} data-testid="lp-contact-first"/>
                <input aria-label="Last name" placeholder="Last name *" value={form.last_name} onChange={(e) => up("last_name", e.target.value)} className={inputCls} data-testid="lp-contact-last"/>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input aria-label="Work email" type="email" placeholder="Work email *" value={form.email} onChange={(e) => up("email", e.target.value)} className={inputCls} data-testid="lp-contact-email"/>
                <input aria-label="Phone" type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => up("phone", e.target.value)} className={inputCls} data-testid="lp-contact-phone"/>
              </div>
              <input aria-label="Organisation" placeholder="Organisation *" value={form.organisation} onChange={(e) => up("organisation", e.target.value)} className={inputCls} data-testid="lp-contact-org"/>
              <div className="grid sm:grid-cols-2 gap-4">
                <select aria-label="Organisation type" value={form.organisation_type} onChange={(e) => up("organisation_type", e.target.value)} className={inputCls} data-testid="lp-contact-type">
                  <option value="council" style={{ color: "#000" }}>Council</option>
                  <option value="private_certifier" style={{ color: "#000" }}>Private Certifier</option>
                  <option value="inspection_business" style={{ color: "#000" }}>Inspection Business</option>
                  <option value="government" style={{ color: "#000" }}>Government</option>
                  <option value="other" style={{ color: "#000" }}>Other</option>
                </select>
                <select aria-label="Team size" value={form.team_size} onChange={(e) => up("team_size", e.target.value)} className={inputCls} data-testid="lp-contact-size">
                  <option value="" style={{ color: "#000" }}>Team size…</option>
                  {["1", "2-5", "6-10", "11-25", "26-50", "51+"].map((s) => <option key={s} value={s} style={{ color: "#000" }}>{s}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-2" aria-label="Modules interested in">
                {MODULES.map((m) => {
                  const on = form.modules.includes(m.name);
                  return (
                    <button type="button" key={m.name} onClick={() => up("modules", on ? form.modules.filter((x) => x !== m.name) : [...form.modules, m.name])}
                      className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${on ? "text-[#0B1020]" : "border-white/15 text-slate-400 hover:border-white/30"}`}
                      style={on ? { background: MINT, borderColor: MINT } : {}} data-testid={`lp-contact-module-${m.name.replace(/\W+/g, "-").toLowerCase()}`}>
                      {m.name}
                    </button>
                  );
                })}
              </div>
              <textarea aria-label="Message" rows={4} placeholder="Message" value={form.message} onChange={(e) => up("message", e.target.value)}
                className="w-full rounded-md border border-white/15 bg-white/5 p-3.5 text-[15px] text-white placeholder:text-slate-600 focus:border-[#00F2C3] focus:outline-none" data-testid="lp-contact-message"/>
              <input type="text" value={form.website_url} onChange={(e) => up("website_url", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true"/>
              <button type="submit" disabled={busy} data-testid="lp-contact-submit"
                className="w-full h-13 min-h-[52px] rounded-md font-semibold text-[15px] inline-flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: MINT, color: NAVY }}>
                {busy ? <Loader2 size={17} className="animate-spin"/> : <Send size={16}/>}Send enquiry
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </Section>
  );
};

/* ---------------------------------------------------------------- FAQ */
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left" data-testid={`lp-faq-${q.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
        <span className="font-semibold text-white text-[15px]" style={{ fontFamily: "Outfit, sans-serif" }}>{q}</span>
        <ChevronDown size={18} className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && <p className="pb-5 text-sm text-slate-400 leading-relaxed -mt-1">{a}</p>}
    </div>
  );
};

/* ---------------------------------------------------------------- page */
export default function LandingPage() {
  const { user } = useAuth();
  return (
    <div id="top" style={{ background: NAVY, fontFamily: "'DM Sans', sans-serif" }} className="min-h-screen antialiased">
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet"/>
      <Header authed={!!user}/>

      {/* HERO */}
      <div style={{ background: "radial-gradient(ellipse 90% 60% at 50% -10%, rgba(30,27,75,0.9) 0%, #0B1020 65%)" }}>
        <Section className="pt-20 pb-16 sm:pt-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-3xl mx-auto">
            <p className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 text-xs font-semibold text-slate-300 mb-8">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: MINT }}/>One Hub. Complete Compliance.
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="lp-hero-h1">
              Compliance Inspections.<br/><span style={{ color: MINT }}>Smarter.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">One intelligent platform for inspections, reporting, regulatory workflows and compliance management.</p>
            <p className="mt-4 text-[15px] text-slate-400 leading-relaxed max-w-2xl mx-auto">
              Compliance Hub gives inspectors, certifiers, councils and compliance teams the tools to complete inspections in the field, capture evidence, generate professional documentation and manage compliance workflows from one secure platform.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              {user ? (
                <Link to="/select-module" data-testid="lp-hero-open-app" className="w-full sm:w-auto px-8 py-3.5 rounded-md font-semibold transition-all hover:opacity-90 inline-flex items-center justify-center gap-2" style={{ background: MINT, color: NAVY }}>
                  Open Compliance Hub<ArrowRight size={17}/>
                </Link>
              ) : (
                <>
                  <Link to="/signup" data-testid="lp-hero-get-started" className="w-full sm:w-auto px-8 py-3.5 rounded-md font-semibold transition-all hover:opacity-90 inline-flex items-center justify-center gap-2" style={{ background: MINT, color: NAVY }}>
                    Get Started<ArrowRight size={17}/>
                  </Link>
                  <a href="#platform" className="w-full sm:w-auto px-8 py-3.5 rounded-md font-semibold border border-white/20 text-white hover:bg-white/5 transition-all text-center">Explore the Platform</a>
                </>
              )}
            </div>
            {!user && <p className="mt-5 text-sm text-slate-500">Already have an account? <Link to="/login" className="font-semibold underline" style={{ color: MINT }} data-testid="lp-hero-login">Log In</Link></p>}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="mt-16">
            <HeroDemo variant="captioned"/>
          </motion.div>
        </Section>
      </div>

      {/* TRUST BAR */}
      <Section className="py-10 border-y border-white/5">
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-[13px] font-medium text-slate-400">
          {["Built for real-world compliance inspections", "Designed by compliance professionals", "Secure cloud platform", "Works online & offline", "AI-assisted workflows", "Australian compliance focused"].map((t) => (
            <span key={t} className="inline-flex items-center gap-2"><Check size={14} style={{ color: MINT }}/>{t}</span>
          ))}
        </div>
      </Section>

      {/* WHAT IS + WORKFLOW */}
      <Section id="platform" className="py-24">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
          <Label>The Platform</Label>
          <H2>One Platform. The Complete Inspection Workflow.</H2>
          <p className="mt-5 text-slate-400 leading-relaxed">Compliance Hub brings inspections, evidence, reporting, communication and regulatory workflows together in one place. From the moment an inspection is created through to final compliance, teams manage their work without switching between systems, documents and spreadsheets.</p>
        </motion.div>
        <motion.div {...fadeUp} className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {WORKFLOW.map((step, i) => (
            <div key={step} className="relative rounded-xl border border-white/10 p-4 sm:p-5 hover:border-white/25 transition-colors" style={{ background: "#13182A" }}>
              <span className="text-[11px] font-bold" style={{ color: MINT }}>{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-1.5 text-sm font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{step}</p>
            </div>
          ))}
        </motion.div>
      </Section>

      {/* MODULES */}
      <Section id="industries" className="py-24 border-t border-white/5">
        <motion.div {...fadeUp} className="max-w-2xl">
          <Label>Modules</Label>
          <H2>One Hub. Multiple Compliance Disciplines.</H2>
          <p className="mt-4 text-slate-400">Each module carries its own checklists, terminology, legislation references, templates and workflows.</p>
        </motion.div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {MODULES.map((m) => (
            <motion.div {...fadeUp} key={m.name} data-testid={`lp-module-${m.name.replace(/\W+/g, "-").toLowerCase()}`}
              className="group rounded-xl border border-white/10 p-7 hover:border-white/25 hover:-translate-y-1 transition-all" style={{ background: "#13182A" }}>
              <span className="inline-flex w-11 h-11 rounded-lg items-center justify-center border border-white/10" style={{ background: "rgba(0,242,195,0.08)" }}>
                <m.icon size={20} style={{ color: MINT }}/>
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{m.name}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FIELD FEATURES */}
      <Section id="features" className="py-24 border-t border-white/5">
        <motion.div {...fadeUp} className="max-w-2xl">
          <Label>In the field</Label>
          <H2>Built for Inspectors in the Field</H2>
        </motion.div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FIELD_FEATURES.map((f) => (
            <motion.div {...fadeUp} key={f.t} className="rounded-xl border border-white/10 p-6 hover:border-white/25 transition-colors" style={{ background: "#13182A" }}>
              <f.icon size={20} style={{ color: MINT }}/>
              <h3 className="mt-4 font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{f.t}</h3>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
          <motion.div {...fadeUp} className="rounded-xl border p-6 flex flex-col justify-center" style={{ background: "rgba(0,242,195,0.06)", borderColor: "rgba(0,242,195,0.3)" }}>
            <p className="font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Less Double Handling. More Inspecting.</p>
            <p className="mt-1.5 text-sm text-slate-400">Evidence, findings and documentation stay connected to the inspection — no re-typing back at the office.</p>
          </motion.div>
        </div>
      </Section>

      {/* AI */}
      <Section id="ai" className="py-24 border-t border-white/5">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <Label>Artificial intelligence</Label>
            <H2>AI That Assists the Inspector</H2>
            <p className="mt-4 text-slate-400 leading-relaxed">AI in Compliance Hub supports professional judgement — it never replaces it.</p>
            <div className="mt-8 space-y-5">
              {[
                ["AI Photo Assistance", "AI can assist in reviewing inspection photos and identifying possible issues."],
                ["AI Compliance Writing", "Generate professional draft compliance comments and report wording."],
                ["AI Checklist Builder", "Describe the inspection required and let AI assist in creating the checklist."],
                ["Guardian Support", "Built-in AI support that helps users navigate the platform and identify technical problems."],
                ["Module-Aware AI", "AI responses adapt to the relevant inspection discipline."],
              ].map(([t, d]) => (
                <div key={t} className="flex gap-3.5">
                  <Sparkles size={17} className="mt-0.5 shrink-0" style={{ color: MINT }}/>
                  <div><p className="font-semibold text-white text-[15px]" style={{ fontFamily: "Outfit, sans-serif" }}>{t}</p>
                    <p className="text-sm text-slate-400 mt-0.5">{d}</p></div>
                </div>
              ))}
            </div>
            <p className="mt-8 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-[13px] text-slate-300" data-testid="lp-ai-disclaimer">
              AI assists inspectors. Final compliance decisions remain with the authorised professional.
            </p>
          </motion.div>
          <motion.div {...fadeUp} aria-hidden="true">
            <div className="rounded-xl border border-white/10 p-6 space-y-4" style={{ background: "#13182A" }}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">AI draft — compliance comment</p>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-[13px] text-slate-300 leading-relaxed">
                The pool gate fails to self-close from a resting position of 100mm, contrary to clause 2.4.2.3 of AS 1926.1-2012. The hinge tension should be adjusted so the gate returns to the closed and latched position from any position…
              </div>
              <div className="flex gap-2">
                <span className="px-3 py-1.5 rounded text-[11px] font-semibold" style={{ background: MINT, color: NAVY }}>Use draft</span>
                <span className="px-3 py-1.5 rounded text-[11px] font-semibold border border-white/15 text-slate-300">Edit</span>
                <span className="px-3 py-1.5 rounded text-[11px] font-semibold border border-white/15 text-slate-300">Discard</span>
              </div>
              <p className="text-[11px] text-slate-600">Inspector review required before use.</p>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* REPORTING */}
      <Section className="py-24 border-t border-white/5">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp} className="order-2 lg:order-1" aria-hidden="true">
            <div className="rounded-xl border border-white/10 p-6" style={{ background: "#13182A" }}>
              <div className="rounded-lg bg-white p-5 shadow-2xl">
                <div className="flex items-center justify-between border-b-2 border-slate-900 pb-3">
                  <div><p className="text-[13px] font-black text-slate-900">NOTICE OF INTENTION</p><p className="text-[10px] text-slate-500">Swimming Pools Act 1992</p></div>
                  <div className="w-10 h-10 rounded bg-[#1E1B4B]"/>
                </div>
                <div className="mt-3 space-y-1.5">
                  {[92, 100, 84, 96, 70].map((w, i) => <div key={i} className="h-1.5 rounded bg-slate-200" style={{ width: `${w}%` }}/>)}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-1.5">
                  {[1, 2, 3].map((n) => <div key={n} className="h-10 rounded bg-slate-100 border border-slate-200"/>)}
                </div>
              </div>
              <p className="mt-4 text-center text-[12px] text-slate-500">Generated in seconds — with your organisation's branding</p>
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="order-1 lg:order-2">
            <Label>Documentation</Label>
            <H2>From Inspection to Professional Report</H2>
            <p className="mt-4 text-slate-400 leading-relaxed">Complete the inspection. Generate the documentation. Send it — without rebuilding the report back at the office.</p>
            <ul className="mt-7 grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-300">
              {["Professional PDF reports", "Compliance documentation", "Non-compliance reports", "Photo reports", "Notices, orders & directions", "Enforcement correspondence", "Organisation branding", "Email directly from the platform", "Email history", "Customer portal access"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0" style={{ color: MINT }}/>{t}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* OFFLINE */}
      <Section className="py-24 border-t border-white/5">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <Label>Field ready</Label>
            <H2>Keep Inspecting — Even Without Reception</H2>
            <p className="mt-4 text-slate-400 leading-relaxed">Poor reception shouldn't stop an inspection. Compliance Hub is built for field conditions with safeguards designed to protect inspection data and recover interrupted synchronisation.</p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-300">
              {["Offline inspections", "Local device saving", "Automatic synchronisation", "Background cloud saving", "Upload queues", "Recovery mechanisms", "Sync status indicators", "Photos retained until uploaded"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0" style={{ color: MINT }}/>{t}</li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} aria-hidden="true">
            <div className="rounded-xl border border-white/10 p-8 text-center" style={{ background: "#13182A" }}>
              <CloudOff size={36} className="mx-auto text-amber-400"/>
              <p className="mt-4 text-white font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>No signal — inspection continues</p>
              <div className="mt-6 space-y-2.5 text-left">
                {[["Checklist answers", "Saved on device"], ["14 photos", "Queued for upload"], ["Signature", "Saved on device"]].map(([a, b]) => (
                  <div key={a} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-4 py-3">
                    <span className="text-sm text-slate-300">{a}</span>
                    <span className="text-[11px] font-semibold" style={{ color: MINT }}>{b}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between rounded-lg border px-4 py-3" style={{ borderColor: "rgba(0,242,195,0.35)", background: "rgba(0,242,195,0.06)" }}>
                  <span className="text-sm text-white font-medium">Reception restored</span>
                  <span className="text-[11px] font-semibold" style={{ color: MINT }}>Syncing… 100%</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* COUNCILS + CERTIFIERS */}
      <Section id="councils" className="py-24 border-t border-white/5">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12">
          <Label>Who it's for</Label>
          <H2>Purpose-Built for Your Organisation</H2>
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div {...fadeUp} className="rounded-xl border border-white/10 p-8" style={{ background: "#13182A" }} data-testid="lp-council-card">
            <Landmark size={24} style={{ color: MINT }}/>
            <h3 className="mt-4 text-xl font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Built for Council Compliance Teams</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
              {["Regulatory tracker — notices, orders, directions, follow-ups, due dates and overdue actions",
                "Automated reminders for approaching due dates",
                "Council-specific workflows — no unnecessary private-certifier steps",
                "Organisation-controlled regulatory templates",
                "Manage officers under one organisation",
                "Detailed records of inspections and regulatory actions"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0" style={{ color: MINT }}/>{t}</li>
              ))}
            </ul>
            <p className="mt-5 text-[12px] text-slate-500">Integration capability is being developed for council systems including records management, property systems and enterprise platforms.</p>
          </motion.div>
          <motion.div {...fadeUp} className="rounded-xl border border-white/10 p-8" style={{ background: "#13182A" }} data-testid="lp-certifier-card">
            <BadgeCheck size={24} style={{ color: MINT }}/>
            <h3 className="mt-4 text-xl font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Built for Private Certifiers</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
              {["Inspection management with statutory deadline tracking",
                "Council notification workflows — the relevant NSW council can be identified automatically from property details",
                "Certifier profile & business branding on documents",
                "Compliance and non-compliance documentation",
                "Reinspection workflows & customer communication",
                "Team accounts and subscription management"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0" style={{ color: MINT }}/>{t}</li>
              ))}
            </ul>
            <p className="mt-5 text-[12px] text-slate-500">Automated council detection streamlines the workflow — verifying statutory correspondence remains the certifier's responsibility.</p>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="mt-10 flex flex-wrap justify-center gap-2.5">
          {AUDIENCES.map((a) => (
            <span key={a} className="px-4 py-2 rounded-full border border-white/10 text-[13px] text-slate-300" style={{ background: "#13182A" }}>{a}</span>
          ))}
        </motion.div>
      </Section>

      {/* CHECKLISTS + PORTAL + TEAMS */}
      <Section className="py-24 border-t border-white/5">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            [Workflow, "Your Inspections. Your Workflows.", "Build checklists from scratch — sections, custom questions, photo requirements, mandatory fields and rich-text instructions. Or let AI assist. Templates are shared organisation-wide."],
            [FileStack, "Better Communication With Customers", "Customers receive secure links to view inspection results, reports, documents and permitted photos — no account required. Reinspection links where enabled."],
            [Users, "Built for One Inspector or an Entire Team", "Solo professionals run their own organisation. Owners invite team members who inherit modules, templates, subscription and settings — with role-based permissions."],
          ].map(([Icon, t, d]) => (
            <motion.div {...fadeUp} key={t} className="rounded-xl border border-white/10 p-7" style={{ background: "#13182A" }}>
              <Icon size={22} style={{ color: MINT }}/>
              <h3 className="mt-4 text-lg font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{t}</h3>
              <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SECURITY */}
      <Section id="security" className="py-24 border-t border-white/5">
        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <Label>Security</Label>
            <H2>Compliance Data Deserves Serious Security</H2>
            <p className="mt-4 text-slate-400 leading-relaxed">Inspection records are statutory records. Compliance Hub protects them with layered, organisation-separated security.</p>
            <p className="mt-6"><a href="#contact" className="text-sm font-semibold underline" style={{ color: MINT }}>Learn About Security →</a></p>
          </motion.div>
          <motion.div {...fadeUp} className="grid sm:grid-cols-2 gap-3">
            {["Secure authentication", "Optional MFA / 2FA", "Login history", "Session management", "Brute-force protection", "Private cloud object storage", "Organisation data separation", "Audit trails", "Role-based permissions"].map((t) => (
              <div key={t} className="flex items-center gap-2.5 rounded-lg border border-white/10 px-4 py-3.5 text-sm text-slate-200" style={{ background: "#13182A" }}>
                <Lock size={14} style={{ color: MINT }}/>{t}
              </div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* BEFORE VS AFTER */}
      <Section className="py-24 border-t border-white/5">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12">
          <Label>The difference</Label>
          <H2>Less Double Handling. More Inspecting.</H2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div {...fadeUp} className="rounded-xl border border-white/10 p-7" style={{ background: "#0E1526" }}>
            <p className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Traditional workflow</p>
            <div className="flex flex-wrap gap-2">
              {["Inspection", "Paper notes", "Photos stored separately", "Return to office", "Re-type findings", "Create report", "Find email", "Send documents", "Spreadsheet reminder", "Follow-up"].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded border border-white/10 text-[12px] text-slate-500">{t}</span>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="rounded-xl border p-7" style={{ background: "#13182A", borderColor: "rgba(0,242,195,0.35)" }}>
            <p className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: MINT }}>Compliance Hub</p>
            <div className="flex flex-wrap gap-2">
              {["Inspection", "Evidence captured", "Compliance recorded", "Documentation generated", "Sent", "Tracked"].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded text-[12px] font-medium text-white border" style={{ background: "rgba(0,242,195,0.08)", borderColor: "rgba(0,242,195,0.25)" }}>{t}</span>
              ))}
            </div>
            <p className="mt-5 text-sm text-slate-400">One system. One workflow. Everything connected to the inspection.</p>
          </motion.div>
        </div>
      </Section>

      {/* PRICING */}
      <div className="border-t border-white/5"><PricingSection/></div>

      {/* FAQ */}
      <Section id="faq" className="py-24 border-t border-white/5">
        <div className="grid lg:grid-cols-3 gap-10">
          <motion.div {...fadeUp}>
            <Label>FAQ</Label>
            <H2>Common Questions</H2>
            <p className="mt-4 text-slate-400 text-sm">Something else? <a href="#contact" className="underline" style={{ color: MINT }}>Contact us</a>.</p>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-2">
            {FAQS.map(([q, a]) => <FaqItem key={q} q={q} a={a}/>)}
          </motion.div>
        </div>
      </Section>

      {/* ABOUT */}
      <Section className="py-20 border-t border-white/5">
        <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
          <Label>About</Label>
          <H2>Built From Real Inspection Experience</H2>
          <p className="mt-5 text-slate-400 leading-relaxed">Compliance Hub was created from real-world compliance inspection experience — over 10 years of inspection and compliance work — with the goal of making inspection work simpler, faster and more consistent. The platform focuses on field inspections, regulatory workflows, documentation, evidence, automation and practical inspector usability.</p>
        </motion.div>
      </Section>

      {/* CONTACT */}
      <div className="border-t border-white/5"><ContactSection/></div>

      {/* FINAL CTA */}
      <Section className="py-24 border-t border-white/5">
        <motion.div {...fadeUp} className="rounded-2xl border p-10 sm:p-16 text-center" style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #13182A 100%)", borderColor: "rgba(0,242,195,0.25)" }}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Ready to Simplify Compliance?</h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">Bring inspections, evidence, reporting and compliance management together in one platform.</p>
          <p className="mt-2 text-sm text-slate-500">Inspect. Capture. Report. Manage.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/signup" data-testid="lp-final-get-started" className="px-8 py-3.5 rounded-md font-semibold transition-all hover:opacity-90" style={{ background: MINT, color: NAVY }}>Get Started</Link>
            <Link to="/login" className="px-8 py-3.5 rounded-md font-semibold border border-white/20 text-white hover:bg-white/5 transition-all">Log In</Link>
            <a href="#contact" className="px-8 py-3.5 rounded-md font-semibold border border-white/20 text-white hover:bg-white/5 transition-all">Contact Compliance Hub</a>
          </div>
        </motion.div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 px-5 sm:px-8 py-14" style={{ background: "#080B16" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2">
              <div className="flex items-center gap-2.5">
                <img src="/ch-logo.png" alt="Compliance Hub" className="h-9 w-auto object-contain"/>
                <span className="text-white font-bold text-[15px] tracking-wide" style={{ fontFamily: "Outfit, sans-serif" }}>COMPLIANCE <span style={{ color: MINT }}>HUB</span></span>
              </div>
              <p className="mt-4 text-sm text-slate-500 max-w-xs">One Hub. Complete Compliance. Inspection and compliance management for councils, certifiers and compliance professionals.</p>
            </div>
            {[
              ["Product", [["Platform", "#platform"], ["Features", "#features"], ["Modules", "#industries"], ["Pricing", "#pricing"], ["Security", "#security"]]],
              ["Solutions", [["Councils", "#councils"], ["Private Certifiers", "#councils"], ["Inspection Businesses", "#industries"]]],
              ["Support", [["Help Centre", "/help"], ["Contact", "#contact"], ["Log In", "/login"]]],
            ].map(([title, links]) => (
              <div key={title}>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{title}</p>
                <ul className="space-y-2">
                  {links.map(([label, href]) => (
                    <li key={label}>{href.startsWith("/")
                      ? <Link to={href} className="text-sm text-slate-500 hover:text-white transition-colors">{label}</Link>
                      : <a href={href} className="text-sm text-slate-500 hover:text-white transition-colors">{label}</a>}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} Compliance Hub. All rights reserved.</p>
            <div className="flex gap-5">
              <Link to="/privacy" className="text-xs text-slate-500 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-xs text-slate-500 hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
